import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MentorStory,
  MentorStoryImage,
  deleteMentorStoryImage,
  getMentorStories,
  getMentorStoryPresignedUrl,
  saveMentorStory,
} from "@/lib/api/mentor-stories";

const MENTOR_STORIES_KEY = (mentorId: string) => ["mentor-stories", mentorId];

export const useMentorStories = (mentorId: string) => {
  return useQuery({
    queryKey: MENTOR_STORIES_KEY(mentorId),
    queryFn: () => getMentorStories(mentorId),
    select: (data) => data[0] ?? null,
    enabled: !!mentorId,
  });
};

export const useSaveMentorStory = (mentorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { storyType: string; images: MentorStoryImage[] }) =>
      saveMentorStory(mentorId, body),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: MENTOR_STORIES_KEY(mentorId),
      });

      const previousStories = queryClient.getQueryData<MentorStory[]>(
        MENTOR_STORIES_KEY(mentorId),
      );

      return { previousStories };
    },
    onError: (_err, _, context) => {
      queryClient.setQueryData(
        MENTOR_STORIES_KEY(mentorId),
        context?.previousStories,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: MENTOR_STORIES_KEY(mentorId),
      });
    },
  });
};

export const useDeleteMentorStoryImage = (mentorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ imageKey }: { imageKey: string }) =>
      deleteMentorStoryImage(mentorId, imageKey),
    onMutate: async ({ imageKey }) => {
      await queryClient.cancelQueries({
        queryKey: MENTOR_STORIES_KEY(mentorId),
      });

      const previousStories = queryClient.getQueryData<MentorStory[]>(
        MENTOR_STORIES_KEY(mentorId),
      );

      queryClient.setQueryData<MentorStory[]>(
        MENTOR_STORIES_KEY(mentorId),
        (old) =>
          old?.map((story) => ({
            ...story,
            images: story.images.filter((img) => img.key !== imageKey),
          })) ?? [],
      );

      return { previousStories };
    },
    onError: (_err, _, context) => {
      queryClient.setQueryData(
        MENTOR_STORIES_KEY(mentorId),
        context?.previousStories,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: MENTOR_STORIES_KEY(mentorId),
      });
    },
  });
};

export const usePresignedStoryUrl = (mentorId: string) => {
  return useMutation({
    mutationKey: ["mentor-story-presigned-url"],
    mutationFn: ({ filename, contentType }: { filename: string; contentType: string }) =>
      getMentorStoryPresignedUrl(mentorId, filename, contentType),
  });
};
